import { useId, useState } from 'react';

import { rangeClasses } from '@/components/bedrock';
import { useT } from '@/core/I18nProvider';
import { formatLocaleNumber } from '@/i18n/format';

import { ShareSlider } from './ShareSlider';
import { estimateMix, normalizeInteractions, normalizeSharePercent } from './uc-estimate';
import { UcResultPanel } from './UcResultPanel';

import type { ReactElement } from 'react';

/**
 * Posted-price usage calculator (ADR-0442) — an interactive "which plan fits
 * me?" estimator for the marketing `/pricing` page.
 *
 * An interaction costs one of exactly four posted prices, set by what it DID:
 * 3 to answer, +3 if it took an action, +6 if it looked something up. So the
 * calculator is a **flag builder**, not a weight picker: the buyer states their
 * monthly interaction volume and what share of those interactions reach each
 * rung, and the total is EXACT — there is no "typical weight", no token
 * component, and nothing to recalibrate later. The three retired controls
 * (light/typical/heavy, and Managed vs BYOK) both described a measured price
 * that no longer exists; BYOK now pays the same ladder, because the price is for
 * what the interaction did, whoever paid the model bill.
 *
 * SSR-safe: renders a sensible default state in the SSG output, fully
 * interactive after hydration — no `window`/`document` access at render, no
 * motion, so it is reduced-motion-respectful by construction. RTL-safe: the
 * native range inputs inherit `direction` from `<html dir>`, so their fill
 * direction follows the document; all spacing uses logical Tailwind utilities.
 */

/**
 * Widest interactions the slider expresses. Chosen so the slider can reach
 * Enterprise at EVERY mix: at the cheapest rung (3 UC) this is 450,000 UC, past
 * Scale's 360,000 allowance — so a buyer whose interactions are all plain
 * answers can still slide off the end of self-serve.
 */
const MAX_INTERACTIONS = 150_000;
const STEP = 1_000;

/**
 * The default slider positions. These are a STARTING POINT for the reader to
 * move, not a published claim about a typical workload — posted pricing has no
 * typical weight, and asserting one here would smuggle the retired hedge back
 * in. They are set to land the default state on a mid-ladder self-serve plan so
 * the SSG output shows the control in a meaningful, non-degenerate position.
 */
const DEFAULT_INTERACTIONS = 5_000;
const DEFAULT_ACTED_PERCENT = 25;
const DEFAULT_LOOKED_UP_PERCENT = 25;

function clampInteractions(value: number): number {
  return Math.min(MAX_INTERACTIONS, normalizeInteractions(value));
}

export function UcCalculator(): ReactElement {
  const { t, locale } = useT();
  const [interactions, setInteractions] = useState<number>(DEFAULT_INTERACTIONS);
  const [actedPercent, setActedPercent] = useState<number>(DEFAULT_ACTED_PERCENT);
  const [lookedUpPercent, setLookedUpPercent] = useState<number>(DEFAULT_LOOKED_UP_PERCENT);

  const sliderLabelId = useId();
  const numberInputId = useId();
  const resultId = useId();

  // The estimate is computed here and passed down: `UcResultPanel` derives the
  // recommended tier from it, so the tier lookup lives with the panel that
  // renders it rather than being threaded through as a second prop.
  const estimate = estimateMix({ interactions, actedPercent, lookedUpPercent });
  const usageCredits = estimate.usageCredits;

  const fmtCount = (n: number): string => formatLocaleNumber(n, locale);
  const fmtPercent = (percent: number): string =>
    formatLocaleNumber(percent / 100, locale, { style: 'percent' });

  return (
    <section
      id="uc-calculator"
      aria-labelledby={`${resultId}-heading`}
      aria-label={t('pricing.calculator.regionLabel')}
      className="border-b border-[color:var(--bd-line-3)] bg-[color:var(--bd-page)] px-6 py-16 sm:py-20"
    >
      <div className="mx-auto max-w-5xl">
        <h2
          id={`${resultId}-heading`}
          className="text-balance text-[clamp(1.75rem,3vw,2.35rem)] font-semibold leading-[1.08] tracking-[-0.04em] text-[color:var(--bd-ink)]"
        >
          {t('pricing.calculator.heading')}
        </h2>
        <p className="mt-3.5 max-w-3xl text-pretty text-[1.05rem] leading-[1.62] text-[color:var(--bd-body)]">
          {t('pricing.calculator.intro')}
        </p>

        <div className="mt-10 grid grid-cols-1 items-start gap-6 lg:grid-cols-2">
          {/* Controls */}
          <div className="rounded-[var(--bd-radius)] border border-[color:var(--bd-line)] bg-gradient-to-b from-[color:var(--bd-raise)] to-[color:var(--bd-page)] p-6 sm:p-8">
            <label
              id={sliderLabelId}
              htmlFor={`${sliderLabelId}-range`}
              className="block text-sm font-medium text-[color:var(--bd-ink)]"
            >
              {t('pricing.calculator.slider.label')}
            </label>
            <output
              htmlFor={`${sliderLabelId}-range`}
              className="mt-2 block text-3xl font-semibold tabular-nums text-[color:var(--bd-ink)]"
            >
              {fmtCount(interactions)}
              {interactions >= MAX_INTERACTIONS ? '+' : ''}{' '}
              <span className="text-base font-normal text-[color:var(--bd-muted)]">
                {t('pricing.calculator.interactionsUnit')}
              </span>
            </output>
            <input
              id={`${sliderLabelId}-range`}
              type="range"
              min={0}
              max={MAX_INTERACTIONS}
              step={STEP}
              value={interactions}
              aria-labelledby={sliderLabelId}
              aria-valuetext={t('pricing.calculator.slider.valueText', {
                interactions: fmtCount(interactions),
                uc: fmtCount(usageCredits),
              })}
              onChange={(e) => {
                setInteractions(clampInteractions(Number(e.target.value)));
              }}
              className={rangeClasses({ className: 'mt-4' })}
            />

            <div className="mt-6">
              <label
                htmlFor={numberInputId}
                className="block text-sm font-medium text-[color:var(--bd-ink)]"
              >
                {t('pricing.calculator.input.label')}
              </label>
              <input
                id={numberInputId}
                type="number"
                inputMode="numeric"
                min={0}
                max={MAX_INTERACTIONS}
                step={STEP}
                value={interactions}
                autoComplete="off"
                onChange={(e) => {
                  setInteractions(clampInteractions(Number(e.target.value)));
                }}
                className="mt-2 w-full rounded-[11px] border border-[color:var(--bd-line-2)] bg-[color:var(--bd-page)] px-3.5 py-2.5 text-base tabular-nums text-[color:var(--bd-ink)] transition-colors duration-200 hover:border-[color:var(--bd-line-3)] focus-visible:border-[color:var(--bd-blue)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:color-mix(in_srgb,var(--bd-blue)_25%,transparent)]"
              />
            </div>

            {/* The two priced flags — the whole posted ladder, stated in the
                buyer's own terms. No presets: the total is exact once these are
                set, so there is nothing to approximate. */}
            <ShareSlider
              name="acted"
              label={t('pricing.calculator.acted.label')}
              help={t('pricing.calculator.acted.help')}
              displayValue={fmtPercent(actedPercent)}
              valueText={t('pricing.calculator.acted.valueText', {
                percent: fmtPercent(actedPercent),
                interactions: fmtCount(estimate.actedInteractions),
              })}
              value={actedPercent}
              onChange={(percent) => {
                setActedPercent(normalizeSharePercent(percent));
              }}
            />
            <ShareSlider
              name="lookedUp"
              label={t('pricing.calculator.lookedUp.label')}
              help={t('pricing.calculator.lookedUp.help')}
              displayValue={fmtPercent(lookedUpPercent)}
              valueText={t('pricing.calculator.lookedUp.valueText', {
                percent: fmtPercent(lookedUpPercent),
                interactions: fmtCount(estimate.lookedUpInteractions),
              })}
              value={lookedUpPercent}
              onChange={(percent) => {
                setLookedUpPercent(normalizeSharePercent(percent));
              }}
            />
          </div>

          <UcResultPanel estimate={estimate} />
        </div>

        <p className="mt-6 max-w-3xl text-xs text-[color:var(--bd-muted)]">
          {t('pricing.calculator.disclaimer')}
        </p>
      </div>
    </section>
  );
}
